import React from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import MUIDataTable from 'mui-datatables';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	PointElement,
	LineElement,
	Tooltip,
	Legend,
	BarController,
	LineController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import KpiStat from './KpiStat';
import { monthLabel, monthLabelLong, deltaCount } from '../helpers';

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	PointElement,
	LineElement,
	Tooltip,
	Legend,
	BarController,
	LineController
);

const OOS_RED = '#E24B4A';

const useStyles = makeStyles((theme) => ({
	card: { overflow: 'hidden' },
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: theme.spacing(1.5, 2),
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	titleWrap: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
	dot: { width: 8, height: 8, borderRadius: '50%', flex: 'none', backgroundColor: OOS_RED },
	title: { fontSize: 13, fontWeight: 700, letterSpacing: 0.2 },
	body: { padding: theme.spacing(2) },
	chart: { position: 'relative', height: 220, marginBottom: theme.spacing(2) },
	spacer: { height: theme.spacing(1.5) },
	subhead: {
		fontSize: 11,
		fontWeight: 700,
		textTransform: 'uppercase',
		letterSpacing: 0.5,
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(1),
	},
	up: { color: theme.palette.success.main, fontWeight: 600 },
	down: { color: theme.palette.error.main, fontWeight: 600 },
	flat: { color: theme.palette.text.disabled },
	rightHead: {
		textAlign: 'right',
		'& [role="button"]': { justifyContent: 'flex-end' },
	},
}));

// more OOS is bad — positive delta shows red, negative shows green
const invDir = (diff) => {
	if (diff === null || diff === undefined) return 'flat';
	if (diff > 0) return 'down';
	if (diff < 0) return 'up';
	return 'flat';
};

export default function OOSModule({ data, loading, language, prevMonthKey }) {
	const classes = useStyles();
	const theme = useTheme();
	const txt = theme.palette.text.primary;
	const grid = theme.palette.divider;
	const { trend = [], current = {}, previous = {}, breakdown = [] } = data || {};
	const labels = trend.map((t) => monthLabel(t.month));
	const last = trend.length - 1;
	const vsPrev = `${language.vs} ${monthLabelLong(prevMonthKey)}`;

	const countDelta = deltaCount(current.count, previous.count);
	const outletDelta = deltaCount(current.outlets, previous.outlets);
	const productDelta = deltaCount(current.products, previous.products);

	const chartData = {
		labels,
		datasets: [
			{
				type: 'line',
				label: language.oos_count,
				data: trend.map((t) => t.count),
				borderColor: OOS_RED,
				backgroundColor: 'rgba(226,75,74,0.08)',
				fill: true,
				tension: 0.35,
				borderWidth: 2,
				pointRadius: trend.map((_, i) => (i === last ? 6 : 3)),
				pointBackgroundColor: OOS_RED,
				yAxisID: 'yLeft',
				order: 1,
			},
			{
				type: 'bar',
				label: language.products_affected,
				data: trend.map((t) => t.products),
				backgroundColor: trend.map((_, i) => (i === last ? 'rgba(245,196,0,0.75)' : 'rgba(245,196,0,0.35)')),
				borderRadius: 3,
				yAxisID: 'yRight',
				order: 2,
			},
		],
	};
	const chartOptions = {
		maintainAspectRatio: false,
		interaction: { mode: 'index', intersect: false },
		plugins: {
			legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 }, color: txt } },
			tooltip: {
				titleColor: txt,
				bodyColor: txt,
				callbacks: {
					label: (ctx) => ` ${ctx.dataset.label}: ${ctx.parsed.y == null ? '—' : ctx.parsed.y}`,
				},
			},
			datalabels: { display: false },
		},
		scales: {
			yLeft: { position: 'left', beginAtZero: true, ticks: { precision: 0, color: OOS_RED }, grid: { color: grid } },
			yRight: {
				position: 'right',
				beginAtZero: true,
				grid: { drawOnChartArea: false },
				ticks: { precision: 0, color: txt },
			},
			x: { grid: { display: false }, ticks: { color: txt } },
		},
	};

	const columns = [
		{ name: 'name', label: language.product, options: { sort: true } },
		{
			name: 'outlets',
			label: language.outlets,
			options: {
				sort: true,
				setCellProps: () => ({ align: 'right' }),
				setCellHeaderProps: () => ({ className: classes.rightHead }),
			},
		},
		{
			name: 'count',
			label: language.oos_count,
			options: {
				sort: true,
				setCellProps: () => ({ align: 'right' }),
				setCellHeaderProps: () => ({ className: classes.rightHead }),
			},
		},
		{
			name: 'prev_count',
			label: `${language.oos_count} ${vsPrev}`,
			options: {
				sort: false,
				setCellProps: () => ({ align: 'right' }),
				setCellHeaderProps: () => ({ className: classes.rightHead }),
				customBodyRender: (value, tableMeta) => {
					const d = deltaCount(tableMeta.rowData[2], value);
					return <span className={classes[invDir(d)]}>{(d > 0 ? '+' : '') + d}</span>;
				},
			},
		},
	];
	const tableOptions = {
		selectableRows: 'none',
		responsive: 'standard',
		download: false,
		print: false,
		filter: false,
		search: false,
		viewColumns: false,
		rowsPerPage: 20,
		rowsPerPageOptions: [20, 50, 100],
		elevation: 0,
	};

	return (
		<Paper className={classes.card} elevation={1}>
			<div className={classes.header}>
				<div className={classes.titleWrap}>
					<span className={classes.dot} />
					<Typography className={classes.title}>{language.oos}</Typography>
				</div>
			</div>
			{loading && <LinearProgress />}
			<div className={classes.body}>
				<div className={classes.chart}>
					<Chart type="bar" data={chartData} options={chartOptions} />
				</div>
				<Grid container spacing={1}>
					<Grid item xs={4}>
						<KpiStat
							label={language.oos_count}
							value={current.count || 0}
							delta={(countDelta > 0 ? '+' : '') + countDelta}
							deltaLabel={vsPrev}
							dir={invDir(countDelta)}
						/>
					</Grid>
					<Grid item xs={4}>
						<KpiStat
							label={language.outlets}
							value={current.outlets || 0}
							delta={(outletDelta > 0 ? '+' : '') + outletDelta}
							deltaLabel={vsPrev}
							dir={invDir(outletDelta)}
						/>
					</Grid>
					<Grid item xs={4}>
						<KpiStat
							label={language.products_affected}
							value={current.products || 0}
							delta={(productDelta > 0 ? '+' : '') + productDelta}
							deltaLabel={vsPrev}
							dir={invDir(productDelta)}
						/>
					</Grid>
				</Grid>
				<div className={classes.spacer} />
				<Typography className={classes.subhead}>{language.summary_by_product}</Typography>
				<MUIDataTable data={breakdown} columns={columns} options={tableOptions} />
			</div>
		</Paper>
	);
}
